/**
 * Recipes Flat View
 *
 * Toutes les recettes actives et leurs ingredients dans une seule grille,
 * groupee par recette et exportable en Excel.
 *
 * Repond au besoin operationnel d'avoir la totalite des fiches techniques
 * d'un coup (audit, comparaison catalogue, achats) plutot que recette par
 * recette via le drawer de detail.
 */

import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import {
  DataGrid,
  textColumn,
  numberColumn,
  type DataGridColumn,
  type DataGridGrouping,
} from '@bill/ui';
import { buildExportFileName } from '@/lib/export-filename';
import { useBrandContext } from '@/hooks';
import { useRecipesFlatView } from '../hooks';
import type { RecipeFlatRow } from '@bstock/shared';

interface RecipesFlatViewProps {
  /** Ne charge les donnees que lorsque l'onglet est visible */
  enabled?: boolean;
}

export function RecipesFlatView({ enabled = true }: RecipesFlatViewProps) {
  const { brand } = useBrandContext();
  const { data, isLoading, error } = useRecipesFlatView(enabled);
  const [search, setSearch] = useState('');

  const exportFileName = useMemo(
    () => buildExportFileName('recettes_donnees_a_plat', brand?.name ?? 'export'),
    [brand?.name]
  );

  // Filtrage client : le jeu de donnees est petit (~1400 lignes) et deja
  // entierement charge, inutile de repasser par le serveur.
  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return all;

    return all.filter(
      (r) =>
        r.recipeName.toLowerCase().includes(term) ||
        r.recipeCode.toLowerCase().includes(term) ||
        r.ingredientName.toLowerCase().includes(term) ||
        r.ingredientSku.toLowerCase().includes(term) ||
        r.posProductName.toLowerCase().includes(term)
    );
  }, [data?.rows, search]);

  const columns = useMemo<DataGridColumn<RecipeFlatRow>[]>(
    () => [
      // Ni le code recette (identifiant technique) ni le produit POS ne sont
      // des colonnes : le premier n'apporte rien a la lecture, le second vit
      // dans l'en-tete de groupe. Tous deux restent interrogeables via la
      // recherche.
      textColumn<RecipeFlatRow>({
        id: 'ingredientName',
        header: 'Ingredient',
        accessor: (r) => r.ingredientName,
      }),
      textColumn<RecipeFlatRow>({
        id: 'ingredientSku',
        header: 'SKU',
        accessor: (r) => r.ingredientSku,
        width: '100px',
      }),
      numberColumn<RecipeFlatRow>({
        id: 'quantity',
        header: 'Quantite',
        accessor: (r) => r.quantity,
        align: 'right',
        width: '110px',
      }),
      textColumn<RecipeFlatRow>({
        id: 'unit',
        header: 'Unite',
        accessor: (r) => r.unit,
        width: '80px',
      }),
    ],
    []
  );

  // Le nom de la recette porte l'en-tete de groupe plutot qu'une colonne :
  // inutile de le repeter sur chaque ligne.
  //
  // Le produit POS y est accole UNIQUEMENT quand il apporte quelque chose —
  // le plus souvent il est identique au nom de la recette. La cle de groupe
  // est aussi ce que l'export Excel ecrit dans sa ligne de separation
  // (`node.key`), donc l'information suit dans le fichier sans avoir besoin
  // d'une colonne dediee.
  const grouping = useMemo<DataGridGrouping<RecipeFlatRow>>(
    () => ({
      groupBy: (r: RecipeFlatRow) => {
        if (!r.posProductName) return `${r.recipeName}  (aucun produit POS)`;
        if (r.posProductName === r.recipeName) return r.recipeName;
        return `${r.recipeName}  →  POS : ${r.posProductName}`;
      },
      defaultExpanded: 'none',
      ungroupedLabel: 'Recette sans nom',
      renderGroupHeader: ({ key, rows: groupRows }) => (
        <span>
          {key}
          <span className="ml-2 text-neutral-500">
            ({groupRows.length} {groupRows.length > 1 ? 'ingredients' : 'ingredient'})
          </span>
        </span>
      ),
    }),
    []
  );

  if (error) {
    return (
      <div className="py-8 text-center text-sm text-danger-600">
        Impossible de charger les recettes. Veuillez reessayer.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative w-[340px] flex-shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Rechercher une recette, un ingredient, un SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-neutral-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                       placeholder:text-neutral-400"
          />
        </div>
        {!isLoading && data && (
          <p className="text-sm text-neutral-500 whitespace-nowrap">
            {data.totalRecipes} recettes, {data.totalIngredients} ingredients
            {search.trim() && ` — ${rows.length} ligne${rows.length > 1 ? 's' : ''} affichee${rows.length > 1 ? 's' : ''}`}
          </p>
        )}
      </div>

      <DataGrid<RecipeFlatRow>
        data={rows}
        columns={columns}
        rowKey={(r, i) => `${r.recipeId}-${r.ingredientSku}-${i}`}
        isLoading={isLoading}
        skeletonRows={10}
        grouping={grouping}
        density="compact"
        stickyHeader
        maxHeight="70vh"
        exportable={{
          fileName: exportFileName,
          sheetName: 'Recettes',
          position: 'toolbar',
        }}
        emptyState={
          <div className="py-8 text-center text-sm text-neutral-500">
            {search.trim()
              ? 'Aucun resultat pour cette recherche'
              : 'Aucune recette active'}
          </div>
        }
      />
    </div>
  );
}
